/**
 * Step one: prove you hold an address we were given.
 *
 * The form does not know and cannot find out whether the address is one we
 * hold. It submits, and the next screen appears either way.
 */

import { useState, type FormEvent } from 'react';

import { useTurnstile } from '../hooks/useTurnstile';
import { Notice } from './Notice';
import {
  GATE_EMAIL_LABEL,
  GATE_HEADING,
  GATE_INTRO,
  GATE_SUBMIT,
  TURNSTILE_FAILED_MESSAGE,
} from '../copy';

interface EmailGateProps {
  notice: string | null;
  busy: boolean;
  onSubmit: (email: string, turnstileToken: string) => Promise<void>;
}

export function EmailGate({ notice, busy, onSubmit }: EmailGateProps) {
  const [email, setEmail] = useState('');
  const turnstile = useTurnstile('portal-email');

  const canSubmit = !busy && turnstile.token !== null && email.trim().length > 0;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canSubmit || turnstile.token === null) return;
    const token = turnstile.token;
    turnstile.reset();
    await onSubmit(email.trim(), token);
  }

  return (
    <section className="panel">
      <h1>{GATE_HEADING}</h1>
      <p className="lede">{GATE_INTRO}</p>
      <Notice message={notice} />
      <form onSubmit={handleSubmit} noValidate>
        <label className="field">
          <span className="field-label">{GATE_EMAIL_LABEL}</span>
          <input
            type="email"
            name="email"
            autoComplete="email"
            autoCapitalize="off"
            spellCheck={false}
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>
        <div className="turnstile" ref={turnstile.containerRef} />
        {turnstile.failed ? <Notice message={TURNSTILE_FAILED_MESSAGE} /> : null}
        <button type="submit" className="button-primary" disabled={!canSubmit}>
          {GATE_SUBMIT}
        </button>
      </form>
    </section>
  );
}
